import { useCallback, useState } from 'react';

import updateQuote from '@/shared/api/orders/quotes/updateQuote';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import DialogName from '@/shared/types/dialogName.enum';
import QuoteResponse from '@/shared/types/order/quotes/quoteResponse.type';
import Response from '@/shared/types/response';

import useCart from '../../components/Cart/hooks/useCart';
import UpdateHandlers from '../../types/updateHandlers.type';
import useDefaultSettings from './useDefaultSettings';

type Args = {
  cartState: ReturnType<typeof useCart>;
  dialogProps: UpdateHandlers<unknown>;
  defaultSettingsState: ReturnType<typeof useDefaultSettings>;
  handleResponse: (response: Response<QuoteResponse>) => void;
};

const useQuoteUpdate = ({
  cartState,
  dialogProps,
  defaultSettingsState,
  handleResponse,
}: Args) => {
  const redirectLogin = useRedirectLogin();

  const [shippingPrice, setShippingPrice] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');

  const onQuoteUpdate = useCallback(
    async (value?: {
      shipping_profile_id?: string;
      shipping_profile_zone_id?: string;
      shipping_price?: string;
      discount_percent?: string;
      currency?: string;
    }) => {
      dialogProps.onUpdateDialogState(DialogName.AssignCustomer, {
        loading: true,
      });
      dialogProps.onUpdateDialogState(DialogName.ShipToCustomer, {
        loading: true,
      });
      dialogProps.onUpdateDialogState(DialogName.AddDiscount, {
        loading: true,
      });

      if (value?.shipping_price) setShippingPrice(value?.shipping_price);
      if (value?.discount_percent) setDiscountPercent(value?.discount_percent);

      const response = await updateQuote({
        quoteId: cartState.assignedQuote?.quote[0].order_quote_id as string,
        body: {
          ...defaultSettingsState.defaultSettings,
          products: cartState.productsInCart.map((product) => {
            const price = Array.isArray(product.price)
              ? (product.price.find((item) => item.currency === value?.currency)
                  ?.value as string)
              : product.price;

            return {
              product_id: product.product_id,
              variant_id: product.variant_id,
              product_qty: product.product_qty,
              price,
            };
          }),
          in_customer_id: cartState.assignedCustomer?.in_customer_id as string,
          shipping_profile_id:
            value?.shipping_profile_id ||
            defaultSettingsState.defaultSettings.shipping_profile_id,
          shipping_profile_zone_id:
            value?.shipping_profile_zone_id ||
            defaultSettingsState.defaultSettings.shipping_profile_zone_id,
          shipping_price: value?.shipping_price || shippingPrice || undefined,
          discount_percent:
            value?.discount_percent || discountPercent || undefined,
        },
        onUnauthorized: redirectLogin,
      });

      dialogProps.onUpdateDialogState(DialogName.AssignCustomer, {
        loading: false,
        open: false,
      });
      dialogProps.onUpdateDialogState(DialogName.ShipToCustomer, {
        loading: false,
        open: false,
      });
      dialogProps.onUpdateDialogState(DialogName.AddDiscount, {
        loading: false,
        open: false,
      });

      handleResponse(response);
    },
    [
      dialogProps,
      cartState.assignedQuote?.quote,
      cartState.productsInCart,
      cartState.assignedCustomer?.in_customer_id,
      defaultSettingsState.defaultSettings,
      shippingPrice,
      discountPercent,
      redirectLogin,
      handleResponse,
    ],
  );

  return { onQuoteUpdate };
};

export default useQuoteUpdate;
